"use client";

import { yooKassaService } from "../payment-services/yookassa.service";
import { isError } from "@/utils/type-guards";

// Payment widget callback types
interface PaymentOptions {
  [key: string]: unknown;
}

interface PaymentResult {
  status?: string;
  transactionId?: string;
  [key: string]: unknown;
}

interface YooKassaPaymentOptions {
  onSuccess?: (options?: PaymentOptions) => void;
  onFail?: (reason?: string | Error, options?: PaymentOptions) => void;
  onComplete?: (paymentResult?: PaymentResult, options?: PaymentOptions) => void;
}

// YooKassa widget constructor interface
interface YooMoneyCheckoutWidgetConstructor {
  new (config: {
    confirmation_token: string;
    return_url: string;
    customization?: {
      modal?: boolean;
      [key: string]: unknown;
    };
    error_callback?: (error: unknown) => void;
    [key: string]: unknown;
  }): {
    render: () => void;
    [key: string]: unknown;
  };
}

// Declare YooKassa types on window
declare global {
  interface Window {
    YooMoneyCheckoutWidget?: YooMoneyCheckoutWidgetConstructor;
  }
}

export class YooKassaPaymentWidget {
  private async loadYooKassaScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.YooMoneyCheckoutWidget) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://yookassa.ru/checkout-widget/v1/checkout-widget.js";
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("Failed to load YooKassa script"));
      document.head.appendChild(script);
    });
  }

  async initiatePayment(
    tokenCount: number,
    amount: number,
    currency: string,
    userId: string,
    email: string,
    callbacks: YooKassaPaymentOptions
  ) {
    try {
      await this.loadYooKassaScript();

      // Generate unique key for idempotence
      const idempotenceKey = `${userId}-${Date.now()}`;

      // Get token from YooKassa through our service
      const confirmationToken = await yooKassaService.generateToken({
        idempotenceKey,
        amount,
        tokenCount,
        description: `Purchase ${tokenCount} tokens`,
      });

      // Initialize YooKassa widget
      if (!window.YooMoneyCheckoutWidget) {
        throw new Error("YooMoneyCheckoutWidget is not loaded");
      }

      const checkout = new window.YooMoneyCheckoutWidget({
        confirmation_token: confirmationToken,
        return_url: `${window.location.href}?payment_status=success`,
        customization: {
          // Display mode settings
          modal: true,
        },
        error_callback: (error: unknown) => {
          console.error("YooKassa payment error:", error);
          const errorReason = isError(error)
            ? error
            : typeof error === "string"
            ? error
            : "Payment failed";
          callbacks.onFail?.(errorReason, { tokenCount, amount });
        },
      });

      // Open widget
      checkout.render();
    } catch (error) {
      console.error("Failed to initiate YooKassa payment:", error);
      const errorReason = isError(error)
        ? error
        : typeof error === "string"
        ? error
        : "Payment initialization failed";
      callbacks.onFail?.(errorReason, { tokenCount, amount });
    }
  }
}
